import React, { useEffect, useRef, useState, useCallback } from 'react';
import styled from 'styled-components';

// ============================================================================
// Styled Components
// ============================================================================

const Container = styled.div`
  width: 100%;
  height: 100%;
  background: #0a0a0a;
  position: relative;
  overflow: hidden;
  touch-action: none;
`;

const Canvas = styled.canvas`
  width: 100%;
  height: 100%;
  display: block;
`;

const HintText = styled.div`
  position: absolute;
  bottom: 60px;
  left: 50%;
  transform: translateX(-50%);
  color: #444;
  font-size: 12px;
  font-family: 'Inter', sans-serif;
  pointer-events: none;
  text-align: center;
`;

const FPSCounter = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  background: rgba(0, 0, 0, 0.7);
  color: #0f0;
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  padding: 6px 10px;
  border-radius: 4px;
`;

const ModeButtons = styled.div`
  position: absolute;
  bottom: 15px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 8px;
  z-index: 10;
`;

const SmallButton = styled.button<{ $active?: boolean }>`
  background: ${props => props.$active ? 'rgba(0, 191, 255, 0.3)' : 'rgba(0, 0, 0, 0.7)'};
  border: 2px solid ${props => props.$active ? '#00bfff' : '#333'};
  color: ${props => props.$active ? '#00bfff' : '#666'};
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  font-weight: 600;
  padding: 6px 10px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:active {
    transform: scale(0.95);
  }
`;

const ColorPicker = styled.div`
  position: absolute;
  bottom: 55px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 8px;
  background: rgba(0, 0, 0, 0.8);
  padding: 8px 12px;
  border-radius: 12px;
  z-index: 11;
`;

const ColorButton = styled.button<{ $color: string; $active: boolean }>`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${props => props.$color};
  border: 3px solid ${props => props.$active ? '#fff' : 'transparent'};
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: ${props => props.$active ? '0 0 10px ' + props.$color : 'none'};
  
  &:active {
    transform: scale(0.9);
  }
`;

const RGBButton = styled.button<{ $active: boolean }>`
  padding: 4px 10px;
  border-radius: 14px;
  background: ${props => props.$active 
    ? 'linear-gradient(90deg, #ff0000, #ff7f00, #ffff00, #00ff00, #0000ff, #8b00ff)' 
    : 'linear-gradient(90deg, #660000, #663300, #666600, #006600, #000066, #330066)'};
  border: 3px solid ${props => props.$active ? '#fff' : 'transparent'};
  color: ${props => props.$active ? '#fff' : '#888'};
  font-family: 'Inter', sans-serif;
  font-size: 10px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  text-shadow: 0 1px 2px rgba(0,0,0,0.8);
  
  &:active {
    transform: scale(0.9);
  }
`;

// ============================================================================
// WebGL Shaders
// ============================================================================

const vertexShaderSource = `
  attribute vec2 a_position;
  attribute float a_height;
  attribute float a_baseColor;
  
  uniform vec2 u_resolution;
  uniform float u_pointSize;
  uniform float u_time;
  uniform int u_colorMode;
  uniform float u_tiltX;
  uniform float u_tiltY;
  
  varying float v_height;
  varying float v_baseColor;
  varying vec2 v_position;
  
  void main() {
    // Convert to centered normalized coordinates
    vec2 centered = (a_position / u_resolution) * 2.0 - 1.0;
    centered.y *= -1.0; // Flip Y
    
    // Create 3D position (x, y, height as z)
    float z = a_height * 0.02; // Scale height to reasonable z depth
    
    // Apply rotation around X axis (tilt forward/back)
    float cosX = cos(u_tiltX);
    float sinX = sin(u_tiltX);
    float y1 = centered.y * cosX - z * sinX;
    float z1 = centered.y * sinX + z * cosX;
    
    // Apply rotation around Y axis (tilt left/right)
    float cosY = cos(u_tiltY);
    float sinY = sin(u_tiltY);
    float x1 = centered.x * cosY + z1 * sinY;
    float z2 = -centered.x * sinY + z1 * cosY;
    
    // Simple perspective projection (closer = larger)
    float perspective = 1.0 / (1.0 - z2 * 0.3);
    vec2 projected = vec2(x1, y1) * perspective;
    
    gl_Position = vec4(projected, z2 * 0.1, 1.0);
    
    // Point size varies with height and perspective
    gl_PointSize = (u_pointSize + abs(a_height) * 0.5) * perspective;
    
    v_height = a_height;
    v_baseColor = a_baseColor;
    v_position = a_position;
  }
`;

const fragmentShaderSource = `
  precision mediump float;
  
  varying float v_height;
  varying float v_baseColor;
  varying vec2 v_position;
  
  uniform float u_time;
  uniform int u_colorMode;
  uniform vec3 u_primaryColor;
  
  vec3 hsl2rgb(float h, float s, float l) {
    float c = (1.0 - abs(2.0 * l - 1.0)) * s;
    float x = c * (1.0 - abs(mod(h / 60.0, 2.0) - 1.0));
    float m = l - c / 2.0;
    vec3 rgb;
    if (h < 60.0) rgb = vec3(c, x, 0.0);
    else if (h < 120.0) rgb = vec3(x, c, 0.0);
    else if (h < 180.0) rgb = vec3(0.0, c, x);
    else if (h < 240.0) rgb = vec3(0.0, x, c);
    else if (h < 300.0) rgb = vec3(x, 0.0, c);
    else rgb = vec3(c, 0.0, x);
    return rgb + m;
  }
  
  void main() {
    float absZ = abs(v_height);
    
    // Very dark background for low pins - true black feel
    if (absZ < 0.4) {
      gl_FragColor = vec4(0.04, 0.04, 0.04, 1.0);
      return;
    }
    
    vec3 color;
    
    if (u_colorMode == 5) {
      // RGB mode - rainbow based on position and time
      float hue = mod((v_position.x + v_position.y) * 0.5 + u_time * 100.0, 360.0);
      float sat = 0.8 + absZ * 0.04;
      float lit = 0.15 + absZ * 0.15;
      color = hsl2rgb(hue, sat, lit);
    } else {
      // Standard color gradient - BRIGHT and punchy
      if (absZ > 2.0) {
        // Full bright color at top
        color = u_primaryColor * 1.2;
      } else if (absZ > 0.8) {
        // Gradient from warm dark to primary
        float t = (absZ - 0.8) / 1.2;
        vec3 warmDark = u_primaryColor * 0.3;
        color = mix(warmDark, u_primaryColor * 1.1, t);
      } else {
        // Low pins - very subtle warm glow
        float t = (absZ - 0.4) / 0.4;
        color = mix(vec3(0.08, 0.06, 0.04), u_primaryColor * 0.3, t);
      }
    }
    
    gl_FragColor = vec4(color, 1.0);
  }
`;

// ============================================================================
// Types
// ============================================================================

type Mode = 'touch' | 'write' | 'zen';
type ColorMode = 'cyan' | 'orange' | 'green' | 'purple' | 'pink' | 'rgb';

interface TouchPoint {
  x: number;
  y: number;
  id: number;
}

interface WebGLPinArtProps {
  width?: number;
  height?: number;
  gridSpacing?: number;
}

// ============================================================================
// Constants
// ============================================================================

const SPRING_STIFFNESS = 0.15;
const SPRING_DAMPING = 0.92;
const NEIGHBOR_INFLUENCE = 0.08;
const TOUCH_RADIUS = 50;
const WRITE_MODE_HOLD = 120;
const WRITE_MODE_FADE = 0.008;
const ULTRA_FINE_SPACING = 8.0; // 8px spacing to match pretext (~4250 pins)

// Bitcoin Conference 2026 deadline - July 24, 2026 00:00:00 UTC
const CONFERENCE_DATE = new Date('2026-07-24T00:00:00Z');

const ZEN_PATTERNS = [
  'countdown', 'breathing', 'fibonacci', 'kaleidoscope', 'interference', 'ripples', 'lissajous',
  'mandala', 'moire', 'plasma', 'harmonics', 'galaxy', 'waves3d', 'heart', 'vortex',
  'rain', 'pendulum', 'bubbles', 'tunnel', 'grid', 'pulse'
] as const;

const ZEN_DESCRIPTIONS: Record<typeof ZEN_PATTERNS[number], string> = {
  countdown: 'Bitcoin Conference 2026',
  breathing: 'Breathe with the ring',
  fibonacci: 'Golden spiral waves',
  kaleidoscope: '8-fold symmetry',
  interference: 'Wave interference',
  ripples: 'Dancing ripples',
  lissajous: 'Lissajous curves',
  mandala: 'Rotating flower',
  moire: 'Moiré patterns',
  plasma: 'Flowing plasma',
  harmonics: 'Harmonic oscillation',
  galaxy: 'Spiral galaxy',
  waves3d: '3D wave field',
  heart: 'Beating heart',
  vortex: 'Swirling vortex',
  rain: 'Gentle rain drops',
  pendulum: 'Swinging pendulums',
  bubbles: 'Rising bubbles',
  tunnel: 'Infinite tunnel',
  grid: 'Breathing grid',
  pulse: 'Radiating pulses'
};

const COLOR_VALUES: Record<Exclude<ColorMode, 'rgb'>, [number, number, number]> = {
  cyan: [0, 0.75, 1],
  orange: [0.97, 0.58, 0.1],
  green: [0, 1, 0.53],
  purple: [0.75, 0.37, 1],
  pink: [1, 0.41, 0.71],
};

// ============================================================================
// Component
// ============================================================================

const WebGLPinArt: React.FC<WebGLPinArtProps> = ({
  width,
  height,
  gridSpacing = ULTRA_FINE_SPACING
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const glRef = useRef<WebGLRenderingContext | null>(null);
  const programRef = useRef<WebGLProgram | null>(null);
  const animationRef = useRef<number>();
  
  const [dimensions, setDimensions] = useState({ width: 400, height: 600 });
  const [fps, setFps] = useState(0);
  const [pinCount, setPinCount] = useState(0);
  const [mode, setMode] = useState<Mode>('touch');
  const [zenPatternIndex, setZenPatternIndex] = useState(0);
  const [colorMode, setColorMode] = useState<ColorMode>('orange');
  const [showColorPicker, setShowColorPicker] = useState(false);
  
  const modeRef = useRef<Mode>('touch');
  const touchesRef = useRef<TouchPoint[]>([]);
  const zenTimeRef = useRef(0);
  const zenPatternRef = useRef(0);
  const timeRef = useRef(0);
  const colorModeRef = useRef<ColorMode>('orange');
  
  // Pin data stored in typed arrays for WebGL
  const positionsRef = useRef<Float32Array | null>(null);
  const heightsRef = useRef<Float32Array | null>(null);
  const velocitiesRef = useRef<Float32Array | null>(null);
  const holdTimesRef = useRef<Float32Array | null>(null);
  const baseColorsRef = useRef<Float32Array | null>(null);
  const gridInfoRef = useRef({ cols: 0, rows: 0 });
  const countdownCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const countdownDataRef = useRef<ImageData | null>(null);
  const lastCountdownSecondRef = useRef<number>(-1);
  
  const frameTimesRef = useRef<number[]>([]);
  const lastFrameRef = useRef(performance.now());
  
  // 3D Tilt state
  const tiltXRef = useRef(0.3); // Default slight forward tilt to see pins
  const tiltYRef = useRef(0);
  const lastTwoFingerRef = useRef<{ x: number, y: number } | null>(null);
  const isTiltingRef = useRef(false);

  // ============================================================================
  // WebGL Setup
  // ============================================================================
  
  const createShader = (gl: WebGLRenderingContext, type: number, source: string): WebGLShader | null => {
    const shader = gl.createShader(type);
    if (!shader) return null;
    
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      console.error('Shader compile error:', gl.getShaderInfoLog(shader));
      gl.deleteShader(shader);
      return null;
    }
    
    return shader;
  };
  
  const createProgram = (gl: WebGLRenderingContext): WebGLProgram | null => {
    const vertexShader = createShader(gl, gl.VERTEX_SHADER, vertexShaderSource);
    const fragmentShader = createShader(gl, gl.FRAGMENT_SHADER, fragmentShaderSource);
    
    if (!vertexShader || !fragmentShader) return null;
    
    const program = gl.createProgram();
    if (!program) return null;
    
    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);
    
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      console.error('Program link error:', gl.getProgramInfoLog(program));
      gl.deleteProgram(program);
      return null;
    }
    
    return program;
  };

  // ============================================================================
  // Initialize Pin Grid
  // ============================================================================
  
  useEffect(() => {
    if (dimensions.width === 0) return;
    
    const padding = 15;
    const areaWidth = dimensions.width - padding * 2;
    const areaHeight = dimensions.height - padding * 2 - 60;
    
    const cols = Math.floor(areaWidth / gridSpacing);
    const rows = Math.floor(areaHeight / gridSpacing);
    const numPins = cols * rows;
    
    console.log(`WebGL Pin Art: ${cols}x${rows} = ${numPins} pins`);
    setPinCount(numPins);
    
    const positions = new Float32Array(numPins * 2);
    const heights = new Float32Array(numPins);
    const velocities = new Float32Array(numPins);
    const holdTimes = new Float32Array(numPins);
    const baseColors = new Float32Array(numPins);
    
    const spacingX = areaWidth / Math.max(1, cols - 1);
    const spacingY = areaHeight / Math.max(1, rows - 1);
    
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const idx = row * cols + col;
        positions[idx * 2] = padding + col * spacingX;
        positions[idx * 2 + 1] = padding + row * spacingY;
        heights[idx] = 0;
        velocities[idx] = 0;
        holdTimes[idx] = 0;
        baseColors[idx] = 0.8 + Math.random() * 0.2;
      }
    }
    
    positionsRef.current = positions;
    heightsRef.current = heights;
    velocitiesRef.current = velocities;
    holdTimesRef.current = holdTimes;
    baseColorsRef.current = baseColors;
    gridInfoRef.current = { cols, rows };
  }, [dimensions, gridSpacing]);

  // ============================================================================
  // Touch Handlers
  // ============================================================================
  
  const handleTouchStart = useCallback((e: TouchEvent) => {
    e.preventDefault();
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const touches: TouchPoint[] = [];
    
    for (let i = 0; i < e.touches.length; i++) {
      const t = e.touches[i];
      touches.push({
        x: (t.clientX - rect.left) / rect.width * dimensions.width,
        y: (t.clientY - rect.top) / rect.height * dimensions.height,
        id: t.identifier
      });
    }
    
    touchesRef.current = touches;
    
    // Two-finger touch starts tilt mode
    if (e.touches.length === 2) {
      const t1 = e.touches[0];
      const t2 = e.touches[1];
      const centerX = (t1.clientX + t2.clientX) / 2;
      const centerY = (t1.clientY + t2.clientY) / 2;
      lastTwoFingerRef.current = { x: centerX, y: centerY };
      isTiltingRef.current = true;
    } else if (e.touches.length === 1 && modeRef.current === 'zen') {
      const nextPattern = (zenPatternRef.current + 1) % ZEN_PATTERNS.length;
      zenPatternRef.current = nextPattern;
      zenTimeRef.current = 0;
      setZenPatternIndex(nextPattern);
    }
  }, [dimensions]);
  
  const handleTouchMove = useCallback((e: TouchEvent) => {
    e.preventDefault();
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const touches: TouchPoint[] = [];
    
    for (let i = 0; i < e.touches.length; i++) {
      const t = e.touches[i];
      touches.push({
        x: (t.clientX - rect.left) / rect.width * dimensions.width,
        y: (t.clientY - rect.top) / rect.height * dimensions.height,
        id: t.identifier
      });
    }
    
    touchesRef.current = touches;
    
    // Two-finger drag controls tilt
    if (e.touches.length === 2 && lastTwoFingerRef.current && isTiltingRef.current) {
      const t1 = e.touches[0];
      const t2 = e.touches[1];
      const centerX = (t1.clientX + t2.clientX) / 2;
      const centerY = (t1.clientY + t2.clientY) / 2;
      
      const deltaX = (centerX - lastTwoFingerRef.current.x) * 0.008;
      const deltaY = (centerY - lastTwoFingerRef.current.y) * 0.008;
      
      // Update tilt (allow full 180° rotation)
      tiltYRef.current = Math.max(-Math.PI, Math.min(Math.PI, tiltYRef.current + deltaX));
      tiltXRef.current = Math.max(-0.3, Math.min(Math.PI, tiltXRef.current + deltaY));
      
      lastTwoFingerRef.current = { x: centerX, y: centerY };
    }
  }, [dimensions]);
  
  const handleTouchEnd = useCallback((e: TouchEvent) => {
    e.preventDefault();
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const remaining: TouchPoint[] = [];
    
    for (let i = 0; i < e.touches.length; i++) {
      const t = e.touches[i];
      remaining.push({
        x: (t.clientX - rect.left) / rect.width * dimensions.width,
        y: (t.clientY - rect.top) / rect.height * dimensions.height,
        id: t.identifier
      });
    }
    
    touchesRef.current = remaining;
    
    // End tilt mode when less than 2 fingers
    if (e.touches.length < 2) {
      lastTwoFingerRef.current = null;
      isTiltingRef.current = false;
    }
  }, [dimensions]);

  // ============================================================================
  // Animation Loop
  // ============================================================================
  
  const render = useCallback(() => {
    const gl = glRef.current;
    const program = programRef.current;
    const canvas = canvasRef.current;
    
    if (!gl || !program || !canvas) {
      animationRef.current = requestAnimationFrame(render);
      return;
    }
    
    const positions = positionsRef.current;
    const heights = heightsRef.current;
    const velocities = velocitiesRef.current;
    const holdTimes = holdTimesRef.current;
    const { cols, rows } = gridInfoRef.current;
    const { width: w, height: h } = dimensions;
    const touches = touchesRef.current;
    const currentMode = modeRef.current;
    
    if (!positions || !heights || !velocities || !holdTimes) {
      animationRef.current = requestAnimationFrame(render);
      return;
    }
    
    const numPins = cols * rows;
    timeRef.current += 0.016;
    
    // Zen mode
    if (currentMode === 'zen') {
      zenTimeRef.current += 0.016;
    }
    
    // Physics update
    const centerX = w / 2;
    const centerY = (h - 80) / 2;
    const maxRadius = Math.min(w, h) * 0.4;
    const t = zenTimeRef.current;
    const pattern = ZEN_PATTERNS[zenPatternRef.current];
    
    // Pre-generate countdown canvas ONCE per second (not per pin!)
    if (pattern === 'countdown' && currentMode === 'zen') {
      const now = Date.now();
      const diff = CONFERENCE_DATE.getTime() - now;
      const currentSecond = Math.floor(diff / 1000);
      
      if (currentSecond !== lastCountdownSecondRef.current && diff > 0) {
        lastCountdownSecondRef.current = currentSecond;
        
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);
        
        if (!countdownCanvasRef.current) {
          countdownCanvasRef.current = document.createElement('canvas');
        }
        const textCanvas = countdownCanvasRef.current;
        
        // Render at 4x resolution for supersampling
        const scale = 4;
        textCanvas.width = cols * scale;
        textCanvas.height = rows * scale;
        const textCtx = textCanvas.getContext('2d');
        
        if (textCtx) {
          // Disable anti-aliasing for crisp edges
          textCtx.imageSmoothingEnabled = false;
          
          textCtx.fillStyle = '#000';
          textCtx.fillRect(0, 0, textCanvas.width, textCanvas.height);
          
          const line1 = `${days}d ${hours}h`;
          const line2 = `${minutes}m ${seconds}s`;
          
          textCtx.fillStyle = '#fff';
          textCtx.textAlign = 'center';
          textCtx.textBaseline = 'middle';
          
          // Larger font for crisp rendering
          const fontSize = Math.min(textCanvas.width / 5, textCanvas.height / 4);
          textCtx.font = `bold ${fontSize}px monospace`;
          
          textCtx.fillText(line1, textCanvas.width / 2, textCanvas.height * 0.35);
          textCtx.fillText(line2, textCanvas.width / 2, textCanvas.height * 0.65);
          
          // Get high-res data then downsample with threshold
          const hiResData = textCtx.getImageData(0, 0, textCanvas.width, textCanvas.height);
          
          // Create final pin-resolution data with binary threshold
          const finalData = new ImageData(cols, rows);
          for (let py = 0; py < rows; py++) {
            for (let px = 0; px < cols; px++) {
              // Sample the center of this pin's corresponding hi-res area
              const hx = Math.floor(px * scale + scale / 2);
              const hy = Math.floor(py * scale + scale / 2);
              const hiIdx = (hy * textCanvas.width + hx) * 4;
              const brightness = hiResData.data[hiIdx];
              
              // Binary threshold: > 128 = fully on, else fully off
              const finalIdx = (py * cols + px) * 4;
              const isOn = brightness > 128 ? 255 : 0;
              finalData.data[finalIdx] = isOn;
              finalData.data[finalIdx + 1] = isOn;
              finalData.data[finalIdx + 2] = isOn;
              finalData.data[finalIdx + 3] = 255;
            }
          }
          countdownDataRef.current = finalData;
        }
      }
    }
    
    for (let i = 0; i < numPins; i++) {
      const px = positions[i * 2];
      const py = positions[i * 2 + 1];
      
      // Update hold time
      if (holdTimes[i] > 0) {
        holdTimes[i] -= 0.016;
        if (holdTimes[i] <= 0) {
          holdTimes[i] = 0;
        }
      }
      
      // Zen mode patterns
      if (currentMode === 'zen') {
        const dx = px - centerX;
        const dy = py - centerY;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const angle = Math.atan2(dy, dx);
        
        let targetZ = 0;
        
        switch (pattern) {
          case 'countdown': {
            // Sample pre-generated text pixel for this pin
            if (countdownDataRef.current) {
              const pinCol = i % cols;
              const pinRow = Math.floor(i / cols);
              const pixelIdx = (pinRow * cols + pinCol) * 4;
              const isOn = countdownDataRef.current.data[pixelIdx] > 128;
              
              // Binary: fully raised or completely flat
              // Subtle pulse only on the raised pins
              if (isOn) {
                const pulse = Math.sin(t * 2) * 0.1 + 0.95;
                targetZ = 6 * pulse;
              }
            }
            break;
          }
          case 'breathing': {
            const breathCycle = 4;
            const breathPhase = (t % breathCycle) / breathCycle;
            const breathRadius = maxRadius * (0.3 + 0.7 * (Math.sin(breathPhase * Math.PI * 2 - Math.PI / 2) * 0.5 + 0.5));
            const ringWidth = 20;
            const distFromRing = Math.abs(dist - breathRadius);
            if (distFromRing < ringWidth) {
              const ringIntensity = 1 - (distFromRing / ringWidth);
              targetZ = ringIntensity * ringIntensity * 6;
            }
            break;
          }
          case 'fibonacci': {
            const phi = 1.618033988749895;
            const spiralAngle = angle + t * 0.5;
            const spiralR = Math.log(dist + 1) / Math.log(phi);
            const wave = Math.sin(spiralR * 2 - spiralAngle * 3 + t * 2);
            const envelope = Math.exp(-dist / (maxRadius * 0.8));
            targetZ = wave * 4 * envelope;
            break;
          }
          case 'kaleidoscope': {
            const segments = 8;
            const segmentAngle = (angle + Math.PI) % (Math.PI * 2 / segments);
            const mirroredAngle = segmentAngle < Math.PI / segments ? segmentAngle : Math.PI * 2 / segments - segmentAngle;
            const radialWave = Math.sin(dist * 0.035 - t * 3);
            const angularWave = Math.sin(mirroredAngle * segments * 2 + t * 2);
            const combined = (radialWave + angularWave) / 2;
            const envelope = Math.exp(-dist / (maxRadius * 0.9));
            targetZ = combined * 4 * envelope;
            break;
          }
          case 'interference': {
            const wave1 = Math.sin(dx * 0.02 + t * 2);
            const wave2 = Math.sin(dy * 0.02 + t * 2.3);
            const wave3 = Math.sin((dx + dy) * 0.015 - t * 1.7);
            const wave4 = Math.sin((dx - dy) * 0.015 + t * 1.5);
            const combined = (wave1 + wave2 + wave3 + wave4) / 4;
            targetZ = combined * 5;
            break;
          }
          case 'ripples': {
            const cx1 = centerX + Math.sin(t * 0.7) * w * 0.2;
            const cy1 = centerY + Math.cos(t * 0.7) * h * 0.15;
            const cx2 = centerX + Math.sin(t * 0.5 + 2) * w * 0.15;
            const cy2 = centerY + Math.cos(t * 0.5 + 2) * h * 0.12;
            
            const d1 = Math.sqrt((px - cx1) ** 2 + (py - cy1) ** 2);
            const d2 = Math.sqrt((px - cx2) ** 2 + (py - cy2) ** 2);
            
            const r1 = Math.sin(d1 * 0.03 - t * 3.5) * Math.exp(-d1 / (w * 0.5));
            const r2 = Math.sin(d2 * 0.035 - t * 2.8) * Math.exp(-d2 / (w * 0.45));
            
            targetZ = (r1 + r2) * 3;
            break;
          }
          case 'lissajous': {
            const lx = centerX + Math.sin(3 * t + 0.5) * w * 0.3;
            const ly = centerY + Math.sin(4 * t) * h * 0.2;
            const lx2 = centerX + Math.sin(5 * t + 0.35) * w * 0.25;
            const ly2 = centerY + Math.sin(7 * t) * h * 0.18;
            
            const dLiss1 = Math.sqrt((px - lx) ** 2 + (py - ly) ** 2);
            const dLiss2 = Math.sqrt((px - lx2) ** 2 + (py - ly2) ** 2);
            
            targetZ = Math.exp(-dLiss1 / 30) * 5 + Math.exp(-dLiss2 / 25) * 4;
            break;
          }
          case 'mandala': {
            const petals = 8;
            const rotatedAngle = angle + t * 0.25;
            const petalAngle = Math.abs(Math.sin(rotatedAngle * petals));
            const petalRadius = maxRadius * (0.3 + 0.6 * petalAngle);
            
            const layer1 = Math.max(0, 1 - Math.abs(dist - petalRadius * 0.5) / 15);
            const layer2 = Math.max(0, 1 - Math.abs(dist - petalRadius * 0.75) / 12);
            const layer3 = Math.max(0, 1 - Math.abs(dist - petalRadius) / 10);
            const centerPulse = Math.exp(-dist / 25) * (Math.sin(t * 2) * 0.3 + 0.7);
            
            targetZ = (layer1 * 2.5 + layer2 * 3 + layer3 * 4 + centerPulse * 5) * 0.4;
            break;
          }
          case 'moire': {
            const freq1 = 0.025;
            const freq2 = 0.026;
            const rotation = t * 0.2;
            
            const rx = dx * Math.cos(rotation) - dy * Math.sin(rotation);
            
            const pattern1 = Math.sin(dx * freq1 * 2 * Math.PI);
            const pattern2 = Math.sin(rx * freq2 * 2 * Math.PI);
            const pattern3 = Math.sin(dist * 0.015 - t);
            
            targetZ = ((pattern1 + pattern2) / 2 + pattern3 * 0.5) * 3;
            break;
          }
          case 'plasma': {
            const plasma1 = Math.sin(px * 0.015 + t);
            const plasma2 = Math.sin(py * 0.015 + t * 1.1);
            const plasma3 = Math.sin((px + py) * 0.012 + t * 0.7);
            const plasma4 = Math.sin(dist * 0.015 - t * 0.5);
            
            const plasma = (plasma1 + plasma2 + plasma3 + plasma4) / 4;
            targetZ = plasma * 4 + 1.5;
            break;
          }
          case 'harmonics': {
            const h1 = Math.sin(dist * 0.025 - t * 2);
            const h2 = Math.sin(dist * 0.05 - t * 3) * 0.5;
            const h3 = Math.sin(dist * 0.075 - t * 4) * 0.25;
            
            const harmonic = h1 + h2 + h3;
            const env = Math.exp(-dist / (maxRadius * 1.2));
            targetZ = harmonic * 3 * env;
            break;
          }
          case 'galaxy': {
            const arms = 2;
            const spiralTightness = 0.006;
            const rotationSpeed = 0.3;
            
            const spiralAngle = angle + t * rotationSpeed;
            let armDist = Infinity;
            
            for (let arm = 0; arm < arms * 3; arm++) {
              const armAngle = (arm / arms) * Math.PI * 2;
              const expectedRadius = (spiralAngle - armAngle + Math.PI * 6) * spiralTightness * maxRadius;
              if (expectedRadius > 0 && expectedRadius < maxRadius * 1.5) {
                armDist = Math.min(armDist, Math.abs(dist - expectedRadius));
              }
            }
            
            const armWidth = 18 + dist * 0.08;
            if (armDist < armWidth) {
              const intensity = (1 - armDist / armWidth) * Math.exp(-dist / (maxRadius * 1.5));
              targetZ = intensity * 5;
            }
            
            const centerGlow = Math.exp(-dist / 30) * 4;
            targetZ = Math.max(targetZ, centerGlow);
            break;
          }
          case 'waves3d': {
            const waveX = Math.sin(px * 0.02 + t * 2);
            const waveY = Math.sin(py * 0.025 + t * 1.7);
            const waveDiag = Math.sin((px - py) * 0.015 + t * 2.3);
            
            const depth = waveX * waveY;
            const surface = (waveX + waveY + waveDiag) / 3;
            
            targetZ = (depth * 2.5 + surface * 2.5) * 0.7;
            break;
          }
          case 'heart': {
            const scale = maxRadius * 0.009;
            const heartX = (px - centerX) / scale;
            const heartY = -(py - centerY) / scale + 10;
            
            const heartEq = Math.pow(heartX * heartX + heartY * heartY - 100, 3) - heartX * heartX * Math.pow(heartY, 3) * 100;
            const heartDist = heartEq / 50000;
            
            const beat = Math.sin(t * 4) * 0.25 + 1;
            
            if (heartDist < 0) {
              const intensity = Math.min(1, -heartDist / 6) * beat;
              targetZ = intensity * 6;
            } else if (heartDist < 3) {
              targetZ = (1 - heartDist / 3) * 2.5 * beat;
            }
            break;
          }
          case 'vortex': {
            const vortexSpeed = t * 2;
            const vortexAngle = angle + vortexSpeed - dist * 0.02;
            const vortexWave = Math.sin(vortexAngle * 4);
            const vortexEnv = Math.exp(-dist / (maxRadius * 0.8));
            
            targetZ = vortexWave * 4 * vortexEnv + Math.exp(-dist / 40) * 3;
            break;
          }
          case 'rain': {
            // Gentle rain drops falling - very soothing
            const dropSpeed = 80;
            const dropSpacing = 50;
            const column = Math.floor(px / dropSpacing);
            const columnOffset = (column * 37) % 100; // Pseudo-random offset per column
            const dropY = ((t * dropSpeed + columnOffset * 5) % (h + 100)) - 50;
            const dropDist = Math.abs(py - dropY);
            const columnCenter = column * dropSpacing + dropSpacing / 2;
            const xDist = Math.abs(px - columnCenter);
            
            if (dropDist < 30 && xDist < 8) {
              const intensity = (1 - dropDist / 30) * (1 - xDist / 8);
              targetZ = intensity * 5;
            }
            break;
          }
          case 'pendulum': {
            // Multiple swinging pendulums - mesmerizing
            const numPendulums = 7;
            const pendulumSpacing = w / (numPendulums + 1);
            
            for (let p = 1; p <= numPendulums; p++) {
              const pendX = p * pendulumSpacing;
              const pendPhase = t * 1.5 + p * 0.4;
              const swing = Math.sin(pendPhase) * maxRadius * 0.5;
              const pendY = centerY + swing;
              const pendDist = Math.sqrt((px - pendX) ** 2 + (py - pendY) ** 2);
              
              if (pendDist < 25) {
                targetZ = Math.max(targetZ, (1 - pendDist / 25) * 6);
              }
              
              // String
              if (Math.abs(px - pendX) < 4 && py < pendY && py > centerY - maxRadius * 0.6) {
                targetZ = Math.max(targetZ, 2);
              }
            }
            break;
          }
          case 'bubbles': {
            // Rising bubbles - calming
            const numBubbles = 12;
            
            for (let b = 0; b < numBubbles; b++) {
              const bubbleX = (b * 67 + 30) % w;
              const bubbleSpeed = 40 + (b % 3) * 15;
              const bubbleSize = 15 + (b % 4) * 8;
              const bubbleY = h - ((t * bubbleSpeed + b * 80) % (h + 100));
              const wobble = Math.sin(t * 2 + b) * 10;
              
              const bubbleDist = Math.sqrt((px - bubbleX - wobble) ** 2 + (py - bubbleY) ** 2);
              
              if (bubbleDist < bubbleSize) {
                // Bubble outline
                const edgeDist = Math.abs(bubbleDist - bubbleSize * 0.7);
                if (edgeDist < 8) {
                  targetZ = Math.max(targetZ, (1 - edgeDist / 8) * 5);
                }
              }
            }
            break;
          }
          case 'tunnel': {
            // Infinite tunnel zooming - hypnotic
            const tunnelSpeed = t * 1.5;
            const numRings = 8;
            
            for (let r = 0; r < numRings; r++) {
              const ringPhase = (tunnelSpeed + r * 0.8) % (numRings * 0.8);
              const ringRadius = ringPhase * maxRadius / 3;
              const ringWidth = 10 + ringPhase * 3;
              const ringDist = Math.abs(dist - ringRadius);
              
              if (ringDist < ringWidth && ringRadius > 10) {
                const intensity = (1 - ringDist / ringWidth) * (1 - ringRadius / (maxRadius * 1.2));
                targetZ = Math.max(targetZ, intensity * 6);
              }
            }
            break;
          }
          case 'grid': {
            // Breathing grid - structured and calming
            const gridSize = 40;
            const breathe = Math.sin(t * 1.5) * 0.5 + 0.5;
            
            const gridX = px % gridSize;
            const gridY = py % gridSize;
            const nearVertical = Math.min(gridX, gridSize - gridX) < 6;
            const nearHorizontal = Math.min(gridY, gridSize - gridY) < 6;
            
            if (nearVertical || nearHorizontal) {
              const edgeDist = nearVertical ? Math.min(gridX, gridSize - gridX) : Math.min(gridY, gridSize - gridY);
              const intensity = (1 - edgeDist / 6) * breathe;
              targetZ = intensity * 5;
            }
            break;
          }
          case 'pulse': {
            // Radiating pulses from multiple points - organic
            const pulsePoints = [
              { x: centerX, y: centerY },
              { x: centerX - w * 0.25, y: centerY - h * 0.2 },
              { x: centerX + w * 0.25, y: centerY + h * 0.15 },
            ];
            
            for (let pp = 0; pp < pulsePoints.length; pp++) {
              const point = pulsePoints[pp];
              const pulseDist = Math.sqrt((px - point.x) ** 2 + (py - point.y) ** 2);
              const pulsePhase = (t * 60 + pp * 50) % 200;
              const pulseDiff = Math.abs(pulseDist - pulsePhase);
              
              if (pulseDiff < 20) {
                const intensity = (1 - pulseDiff / 20) * Math.exp(-pulsePhase / 150);
                targetZ = Math.max(targetZ, intensity * 6);
              }
            }
            break;
          }
        }
        
        velocities[i] += (targetZ - heights[i]) * 0.08;
      }
      
      // Touch influence
      if (currentMode !== 'zen') {
        for (const touch of touches) {
          const tdx = px - touch.x;
          const tdy = py - touch.y;
          const distSq = tdx * tdx + tdy * tdy;
          const radiusSq = TOUCH_RADIUS * TOUCH_RADIUS;
          
          if (distSq < radiusSq) {
            const influence = 1 - distSq / radiusSq;
            
            if (currentMode === 'write') {
              const targetZ = 6 * influence;
              if (targetZ > heights[i]) {
                heights[i] = targetZ;
                velocities[i] = 0;
              }
              holdTimes[i] = WRITE_MODE_HOLD;
            } else {
              // Strong upward push for visible waves
              velocities[i] += influence * 4.0;
            }
          }
        }
      }
      
      // Spring physics
      const springMult = holdTimes[i] > 0 ? 0.005 : (currentMode === 'zen' ? 0.08 : 1);
      velocities[i] -= heights[i] * SPRING_STIFFNESS * springMult;
      velocities[i] *= SPRING_DAMPING;
      heights[i] += velocities[i];
      heights[i] = Math.max(-6, Math.min(10, heights[i]));
    }
    
    // Neighbor influence (simplified for performance)
    if (currentMode !== 'zen') {
      for (let row = 1; row < rows - 1; row++) {
        for (let col = 1; col < cols - 1; col++) {
          const idx = row * cols + col;
          const neighbors = [
            heights[idx - 1],
            heights[idx + 1],
            heights[idx - cols],
            heights[idx + cols]
          ];
          const avg = neighbors.reduce((a, b) => a + b, 0) / 4;
          velocities[idx] += (avg - heights[idx]) * NEIGHBOR_INFLUENCE;
        }
      }
    }
    
    // WebGL rendering
    gl.viewport(0, 0, canvas.width, canvas.height);
    gl.clearColor(0.02, 0.02, 0.02, 1.0); // True black background
    gl.clear(gl.COLOR_BUFFER_BIT);
    
    gl.useProgram(program);
    
    // Set uniforms
    const resolutionLoc = gl.getUniformLocation(program, 'u_resolution');
    const pointSizeLoc = gl.getUniformLocation(program, 'u_pointSize');
    const timeLoc = gl.getUniformLocation(program, 'u_time');
    const colorModeLoc = gl.getUniformLocation(program, 'u_colorMode');
    const primaryColorLoc = gl.getUniformLocation(program, 'u_primaryColor');
    const tiltXLoc = gl.getUniformLocation(program, 'u_tiltX');
    const tiltYLoc = gl.getUniformLocation(program, 'u_tiltY');
    
    gl.uniform2f(resolutionLoc, w, h);
    gl.uniform1f(pointSizeLoc, gridSpacing * 0.2); // Match Canvas2D: small pins with clear spacing
    gl.uniform1f(tiltXLoc, tiltXRef.current);
    gl.uniform1f(tiltYLoc, tiltYRef.current);
    gl.uniform1f(timeLoc, timeRef.current);
    
    const currentColorMode = colorModeRef.current;
    const colorModeInt = currentColorMode === 'rgb' ? 5 : 
      currentColorMode === 'cyan' ? 0 :
      currentColorMode === 'orange' ? 1 :
      currentColorMode === 'green' ? 2 :
      currentColorMode === 'purple' ? 3 : 4;
    gl.uniform1i(colorModeLoc, colorModeInt);
    
    if (currentColorMode !== 'rgb') {
      const [r, g, b] = COLOR_VALUES[currentColorMode];
      gl.uniform3f(primaryColorLoc, r, g, b);
    }
    
    // Position buffer
    const positionBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, positions, gl.DYNAMIC_DRAW);
    
    const positionLoc = gl.getAttribLocation(program, 'a_position');
    gl.enableVertexAttribArray(positionLoc);
    gl.vertexAttribPointer(positionLoc, 2, gl.FLOAT, false, 0, 0);
    
    // Height buffer
    const heightBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, heightBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, heights, gl.DYNAMIC_DRAW);
    
    const heightLoc = gl.getAttribLocation(program, 'a_height');
    gl.enableVertexAttribArray(heightLoc);
    gl.vertexAttribPointer(heightLoc, 1, gl.FLOAT, false, 0, 0);
    
    // Draw
    gl.drawArrays(gl.POINTS, 0, numPins);
    
    // Cleanup
    gl.deleteBuffer(positionBuffer);
    gl.deleteBuffer(heightBuffer);
    
    // FPS tracking
    const now = performance.now();
    frameTimesRef.current.push(now - lastFrameRef.current);
    lastFrameRef.current = now;
    if (frameTimesRef.current.length > 60) frameTimesRef.current.shift();
    
    if (frameTimesRef.current.length >= 30) {
      const avgFrame = frameTimesRef.current.reduce((a, b) => a + b) / frameTimesRef.current.length;
      setFps(Math.round(1000 / avgFrame));
    }
    
    animationRef.current = requestAnimationFrame(render);
  }, [dimensions, gridSpacing]);

  // ============================================================================
  // Effects
  // ============================================================================
  
  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const { clientWidth, clientHeight } = containerRef.current;
        setDimensions({
          width: width || clientWidth || 400,
          height: height || clientHeight || 600
        });
      }
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [width, height]);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    canvas.width = dimensions.width;
    canvas.height = dimensions.height;
    
    const gl = canvas.getContext('webgl', { 
      antialias: false,
      alpha: false,
      preserveDrawingBuffer: false
    });
    
    if (!gl) {
      console.error('WebGL not supported');
      return;
    }
    
    glRef.current = gl;
    programRef.current = createProgram(gl);
    
    return () => {
      if (programRef.current && glRef.current) {
        glRef.current.deleteProgram(programRef.current);
      }
    };
  }, [dimensions]);
  
  useEffect(() => {
    animationRef.current = requestAnimationFrame(render);
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [render]);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    canvas.addEventListener('touchstart', handleTouchStart, { passive: false });
    canvas.addEventListener('touchmove', handleTouchMove, { passive: false });
    canvas.addEventListener('touchend', handleTouchEnd, { passive: false });
    
    return () => {
      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchmove', handleTouchMove);
      canvas.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);
  
  useEffect(() => {
    modeRef.current = mode;
  }, [mode]);
  
  useEffect(() => {
    colorModeRef.current = colorMode;
  }, [colorMode]);
  
  const handleModeChange = useCallback((newMode: Mode) => {
    setMode(newMode);
    if (newMode === 'zen') {
      zenTimeRef.current = 0;
      zenPatternRef.current = 0;
      setZenPatternIndex(0);
    }
  }, []);
  
  const getHintText = () => {
    switch (mode) {
      case 'touch': return 'Touch and swipe to interact';
      case 'write': return 'Draw to make pins stay raised';
      case 'zen': return `${ZEN_DESCRIPTIONS[ZEN_PATTERNS[zenPatternIndex]]} • Tap to change`;
    }
  };
  
  return (
    <Container ref={containerRef}>
      <Canvas ref={canvasRef} />
      <ModeButtons>
        <SmallButton $active={mode === 'touch'} onClick={() => handleModeChange('touch')}>
          👆 Touch
        </SmallButton>
        <SmallButton $active={mode === 'write'} onClick={() => handleModeChange('write')}>
          ✍️ Write
        </SmallButton>
        <SmallButton $active={mode === 'zen'} onClick={() => handleModeChange('zen')}>
          🧘 Zen
        </SmallButton>
        <SmallButton 
          $active={showColorPicker} 
          onClick={() => setShowColorPicker(!showColorPicker)}
          style={{ marginLeft: 'auto' }}
        >
          🎨
        </SmallButton>
      </ModeButtons>
      {showColorPicker && (
        <ColorPicker>
          <ColorButton $color="#00bfff" $active={colorMode === 'cyan'} onClick={() => setColorMode('cyan')} />
          <ColorButton $color="#f7931a" $active={colorMode === 'orange'} onClick={() => setColorMode('orange')} />
          <ColorButton $color="#00ff88" $active={colorMode === 'green'} onClick={() => setColorMode('green')} />
          <ColorButton $color="#bf5fff" $active={colorMode === 'purple'} onClick={() => setColorMode('purple')} />
          <ColorButton $color="#ff69b4" $active={colorMode === 'pink'} onClick={() => setColorMode('pink')} />
          <RGBButton $active={colorMode === 'rgb'} onClick={() => setColorMode('rgb')}>
            RGB
          </RGBButton>
        </ColorPicker>
      )}
      <HintText>{getHintText()}</HintText>
      <FPSCounter>WebGL • {fps} FPS • {pinCount.toLocaleString()} pins</FPSCounter>
    </Container>
  );
};

export default WebGLPinArt;
